/**
 * document.title from resolved terminology, at runtime (T2.12b).
 *
 * Raised in Phase 1: a static <title> in index.html cannot call a hook, so the
 * hardcoded word "Blueprint" survived guardrail 9 there. It is gone now —
 * index.html ships a neutral "Loading…" that names none of the nine overridable
 * terms, and every route replaces it through this hook before the user reads it.
 *
 * Callers pass the words already resolved; this module contains no vocabulary of
 * its own, deliberately, so there is nowhere here for a hardcoded noun to hide.
 */
import { useEffect } from 'react';

/** Shown until React mounts. Must never contain an overridable term. */
export const NEUTRAL_TITLE = 'Loading…';

/**
 * Set the tab title for as long as the calling component is mounted, restoring
 * the previous one on unmount so a transient route cannot leave its title behind.
 *
 * Pass the parts already resolved through useTerms(); they are joined with a
 * middle dot. Empty and nullish parts are dropped, so a caller can pass a name
 * that has not loaded yet without rendering "· undefined".
 */
/**
 * Why `index.html` carries a neutral static title.
 *
 * A static `<title>` cannot call a hook, so a literal "Blueprint" there would
 * show the system's own word to a client who had renamed it — surviving the
 * terminology rule. This hook replaces it once React is mounted and the
 * blueprint's own vocabulary is known.
 *
 * This explanation used to live as an HTML comment in `index.html`. It was
 * moved here on 9 Oct 2026 because link scrapers do not run JavaScript: with no
 * meta tags on the page, WhatsApp had nothing to describe the link with except
 * that comment, and showed it — "…cannot call a hook, so a literal Blueprint
 * here would survive the terminology…" — to anyone the URL was shared with.
 */
export function useDocumentTitle(...parts: (string | null | undefined)[]): void {
  const title = parts.filter((p): p is string => Boolean(p && p.trim())).join(' · ');

  useEffect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}
